// src/utils/uploadUtils.tsx

import { UploadStatus } from "@/features/upload-progress-box/UploadProgressBox";
import {
  addObjectToLocal,
  editObjectInLocalStorageByTimeStamp,
} from "@/persistent-storage/localStorage";
const MAX_CONCURRENT_UPLOADS = 5;
const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300 MB
const ALLOWED_EXTENSIONS = [
  "pdf",
  "jpeg",
  "jpg",
  "png",
  "xml",
  "tiff",
  "tif",
  "docx",
  "pptx",
  "xlsx",
];

import { sendInvoice } from "@/apis/custom/customRoutes";
import { InvoiceType } from "@/lib/constants/invoiceStatus";

export const createUploadNotificationItem = (
  fileName: string,
  tenantId: string | undefined,
): string => {
  const uploadTimestamp = Date.now().toString() + Math.random();
  addObjectToLocal(`invoiceUploadStatus-${tenantId}`, {
    invoiceId: undefined,
    status: UploadStatus.SENT,
    uploadTimestamp,
    fileName,
  });
  return uploadTimestamp;
};

export const updateUploadNotificationItem = (
  invoiceId: string | undefined,
  uploadStatus: UploadStatus,
  uploadTimestamp: string,
  tenantId: string | undefined,
) => {
  try {
    editObjectInLocalStorageByTimeStamp(
      `invoiceUploadStatus-${tenantId}`,
      uploadTimestamp,
      uploadStatus,
      invoiceId,
    );
  } catch (error) {
    console.log("error ", error);
  }
};

function startInterval(action: () => void, interval: number, maxCalls: number) {
  let callCount = 0;
  const intervalId = setInterval(() => {
    if (callCount >= maxCalls) {
      clearInterval(intervalId); // Clear the interval if max calls reached
      console.log("Maximum number of calls reached. Interval cleared.");
      return;
    }

    action(); // Execute the action
    callCount++; // Increment the count
    console.log(`Action executed ${callCount} times.`);
  }, interval);

  // Function to manually stop the interval
  return function stop() {
    clearInterval(intervalId);
    console.log(
      "Interval manually cleared before reaching the maximum number of calls.",
    );
  };
}

export const uploadPdf = async ({
  files,
  tenantId,
  invoiceType = InvoiceType.PAYABLE,
  onSuccess,
  onError,
}: {
  files: FileList;
  tenantId: string | undefined;
  invoiceType?: InvoiceType;
  onSuccess?: (result: any) => void; // Optional success callback
  onError?: (error: any | Error) => void; // Optional error callback
}) => {
  if (!files) {
    return;
  }

  const tooLargeFiles = Array.from(files).filter(
    (file) => file.size >= MAX_FILE_SIZE,
  );

  const invalidFiles = Array.from(files).filter(
    (file) =>
      !ALLOWED_EXTENSIONS.includes(
        file.name.split(".").pop()?.toLowerCase() || "",
      ),
  );

  if (tooLargeFiles.length > 0) {
    if (onError) {
      onError(
        `Dateigröße darf 300MB nicht überschreiten:\n${tooLargeFiles
          .map((file, index) => `${index + 1}.  ${file.name}`)
          .join("\n")}\n\nWir arbeiten daran, größere Dateien zu unterstützen.`,
      );
    }
    return;
  }

  if (invalidFiles.length > 0) {
    if (onError) {
      onError({
        data: {
          code: "FRONTEND",
          message: `Ungültiger Dateityp. Nur PDF-, TIFF/TIF-, JPEG- und PNG-Dateien sind erlaubt:\n${invalidFiles
            .map((file, index) => `${index + 1}. ${file.name}`)
            .join("\n")}`,
        },
      });
    }
    return;
  }

  const result = await uploadAllFiles({
    files,
    tenantId,
    invoiceType,
    onSuccess,
    onError,
  });

  return result.map((res) => res.data?.invoiceId);
};

async function uploadAllFiles({
  files,
  tenantId,
  invoiceType = InvoiceType.PAYABLE,
  onSuccess,
  onError,
}: {
  files: FileList;
  tenantId: string | undefined;
  invoiceType?: InvoiceType;
  onSuccess?: (result: any) => void;
  onError?: (error: any | Error) => void;
}) {
  // Create all notifications immediately
  const notifications = Array.from(files).map((file) => {
    const filename = file.name;
    return createUploadNotificationItem(filename, tenantId);
  });

  let activeCount = 0;
  let currentIndex = 0;

  const uploadPromises = new Array(files.length);

  // Async chain
  const uploadNext = async () => {
    if (currentIndex >= files.length) return;
    const idx = currentIndex++;
    const file = files[idx];
    const timeStamp = notifications[idx];
    const source = "Upload";
    activeCount++;

    try {
      const res = await sendInvoice({
        file,
        source,
        invoiceType,
      });
      uploadPromises[idx] = res;

      if (res?.status === 200) {
        updateUploadNotificationItem(
          res.data?.invoiceId,
          UploadStatus.COMPLETED,
          timeStamp,
          tenantId,
        );
        if (onSuccess) {
          onSuccess(res);
        }
      } else {
        updateUploadNotificationItem(
          undefined,
          UploadStatus.FAILED,
          timeStamp,
          tenantId,
        );
        if (onError) {
          onError(res);
        }
      }
    } catch (error) {
      updateUploadNotificationItem(
        undefined,
        UploadStatus.FAILED,
        timeStamp,
        tenantId,
      );

      if (onError) {
        onError(error);
      }
    } finally {
      activeCount--;
      await uploadNext();
    }
  };

  // start up to MAX_CONCURRENT_UPLOADS of async chains in parallel
  const starters = [];
  for (let i = 0; i < Math.min(MAX_CONCURRENT_UPLOADS, files.length); i++) {
    starters.push(uploadNext());
  }

  // awaits all the chains i.e. all the uploads
  await Promise.all(starters);

  // just returns the resolved promises / responses
  return Promise.all(uploadPromises);
}
